import { RouteComponentProps } from '@reach/router'
import * as React from 'react'
import styled from 'styled-components'
import { Dropdown, ListItem } from '../../../../common/src/components/Dropdown'
import { AlignRight, ErrorText, H3, IntroText, Spacer } from '../../../../common/src/components/core'
import { groupBy } from '../../../../common/src/func'
import { FetchFirmTasksQuery, FetchUserContextQuery, UpdateTasksMutation } from '../../../../common/src/types.gen'
import { Body, ScrollPane } from '../components/containers'
import { AppRouteParams } from '../route'
import AgentsSection from './AgentsSection'
import LaunchDateSection from './LaunchDateSection'
import LeasingSpecialSection from './LeasingSpecialSection'
import MarketingSourcesSection from './MarketingSourcesSection'
import MergeExistingTasksSection from './MergeExistingTasksSection'
import PoliciesSection from './PoliciesSection'

type LeasingTask = UpdateTasksMutation['bulkUpdateTasks'][0]

enum BulkUpdateOption {
  AGENTS = 'AGENTS',
  LAUNCH_DATE = 'LAUNCH_DATE',
  LEASING_SPECIAL = 'LEASING_SPECIAL',
  MARKETING_SOURCES = 'MARKETING_SOURCES',
  MERGE_TASKS = 'MERGE_TASKS',
  POLICIES = 'POLICIES',
}

interface BulkUpdatePageProps extends RouteComponentProps, AppRouteParams {
  user: FetchUserContextQuery['self']
}

interface BulkUpdatePageState {
  selectedBulkUpdateId?: BulkUpdateOption
  updateCompleted: boolean
}

export const bulkUpdateItems: ListItem<BulkUpdateOption>[] = [
  {
    id: BulkUpdateOption.POLICIES,
    name: 'Set a field for many properties',
    disabled: false,
  },
  {
    id: BulkUpdateOption.MARKETING_SOURCES,
    name: 'Add a new marketing source for many properties',
    disabled: false,
  },
  {
    id: BulkUpdateOption.LAUNCH_DATE,
    name: 'Set launch and offboard dates',
    disabled: false,
  },
  {
    id: BulkUpdateOption.AGENTS,
    name: 'Modify an agent across many properties',
    disabled: false,
  },
  {
    id: BulkUpdateOption.LEASING_SPECIAL,
    name: 'Set leasing special for many properties',
    disabled: false,
  },
  {
    id: BulkUpdateOption.MERGE_TASKS,
    name: 'Merge existing tasks',
    disabled: false,
  },
]

export default class BulkUpdatePage extends React.PureComponent<BulkUpdatePageProps, BulkUpdatePageState> {
  constructor(props: BulkUpdatePageProps) {
    super(props)
    this.state = {
      selectedBulkUpdateId: undefined,
      updateCompleted: false,
    }
  }

  private onBulkUpdateItemChange = (item?: ListItem<BulkUpdateOption>) => {
    this.setState({ selectedBulkUpdateId: item?.id })
  }

  private onUpdate = () => {
    this.setState({ updateCompleted: true })
  }

  private renderBulkUpdateContent = (firmId: number) => {
    const { selectedBulkUpdateId } = this.state
    switch (selectedBulkUpdateId) {
      case BulkUpdateOption.MARKETING_SOURCES:
        return <MarketingSourcesSection firmId={firmId} onUpdate={this.onUpdate} />
      case BulkUpdateOption.POLICIES:
        return <PoliciesSection firmId={firmId} onUpdate={this.onUpdate} />
      case BulkUpdateOption.LAUNCH_DATE:
        return <LaunchDateSection firmId={firmId} onUpdate={this.onUpdate} />
      case BulkUpdateOption.AGENTS:
        return <AgentsSection firmId={firmId} onUpdate={this.onUpdate} />
      case BulkUpdateOption.LEASING_SPECIAL:
        return <LeasingSpecialSection firmId={firmId} onUpdate={this.onUpdate} />
      case BulkUpdateOption.MERGE_TASKS:
        return <MergeExistingTasksSection firmId={firmId} onUpdate={this.onUpdate} />
      default:
        return null
    }
  }

  render() {
    const { firmId } = this.props
    const { selectedBulkUpdateId, updateCompleted } = this.state

    if (firmId == null) {
      return null
    }

    return (
      <ScrollPane>
        <BulkUpdateBody>
          <Spacer height={48} />
          {!updateCompleted && (
            <>
              <H3>What would you like to do?</H3>
              <Spacer height={16} />
              <Dropdown
                items={bulkUpdateItems}
                selectedItemId={selectedBulkUpdateId}
                onSelectedItemChange={this.onBulkUpdateItemChange}
                width={480}
              />
              <Spacer height={48} />
            </>
          )}
          {this.renderBulkUpdateContent(Number(firmId))}
        </BulkUpdateBody>
      </ScrollPane>
    )
  }
}

const BulkUpdateBody = styled(Body)`
  margin: auto;
`

/**
 * Print the error text in pink ("coral") just beneath a form field
 */
export function renderFieldError(error: string) {
  return (
    <AlignRight>
      <ErrorText>{error}</ErrorText>
      <Spacer height={16} />
    </AlignRight>
  )
}

export function getTasksNotUpdated(
  allTasks: LeasingTask[],
  selectedTaskIds: Set<string | number>,
  updatedTasks: LeasingTask[]
) {
  const tasksById = groupBy(allTasks, t => t.id)

  const selectedTaskIdsArray = Array.from(selectedTaskIds).map(s => parseInt(s.toString(), 10))
  const updatedTaskIds = new Set(updatedTasks.map(task => task.id))
  const tasksIdsNotUpdated = selectedTaskIdsArray.filter(selectedTaskId => !updatedTaskIds.has(selectedTaskId))
  return tasksIdsNotUpdated.flatMap(taskId => tasksById[taskId]).filter(t => t != null)
}

export function renderTasksList(tasks: (LeasingTask | FetchFirmTasksQuery['firm']['tasks'][0])[]) {
  return tasks.map(task => (
    <span key={task.id}>
      <IntroText color="midway">{task.displayName}</IntroText>
      <Spacer height={12} />
    </span>
  ))
}

export enum Tab {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  PROPERTIES = 'PROPERTIES',
}
